import { formatPostalCode } from "@/Helpers/formatPostalCode";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";
import { bookShipmentFindSavedLocationIndex } from "../Helpers/bookShipmentFindSavedLocationIndex";
import { initialBookShipmentSliceState } from "../Helpers/bookShipmentEmptyState";
import { bookShipmentDropHiddenDeadline } from "./bookShipmentDropHiddenDeadline";
import { removeUnavailableAccessorialsForLocation } from "@/Shared/Locations/Helpers/recommendedAccessorialsForLocation";
import { v4 as uuidv4 } from "uuid";

export function bookShipmentResetStateToModify(payload: {
  shipment: BookShipmentShipmentType | PreBookingShipment;
  savedLocations: Array<SavedLocation>;
}) {
  const newShipment = payload.shipment;
  const savedLocations = payload.savedLocations;
  const newState = initialBookShipmentSliceState();

  newState.shipment = {
    ...newShipment,
    lineItems: newShipment.lineItems,
    shipmentId: uuidv4(),
    selectedQuote: undefined,
    cheapestQuote: undefined,
  };

  // The postal code which comes from the shipment isn't formatted
  const newPickupPostalCode = newShipment.pickupLocation.address.postalCode;
  if (newPickupPostalCode !== undefined) {
    newState.shipment = {
      ...newShipment,
      pickupLocation: {
        ...newShipment.pickupLocation,
        address: {
          ...newShipment.pickupLocation.address,
          postalCode: formatPostalCode(newPickupPostalCode),
        },
      },
    };
  }

  const newDeliveryPostalCode = newShipment.deliveryLocation.address.postalCode;
  if (newDeliveryPostalCode !== undefined) {
    newState.shipment = {
      ...newShipment,
      deliveryLocation: {
        ...newShipment.deliveryLocation,
        address: {
          ...newShipment.deliveryLocation.address,
          postalCode: formatPostalCode(newDeliveryPostalCode),
        },
      },
    };
  }

  const pickupIndex = bookShipmentFindSavedLocationIndex(
    newShipment.pickupLocation,
    savedLocations,
  );
  const deliveryIndex = bookShipmentFindSavedLocationIndex(
    newShipment.deliveryLocation,
    savedLocations,
  );

  if (pickupIndex !== -1) {
    // We found a match for the pickup location
    newState.enteringNewPickup = false;
    newState.selectedPickupSavedLocationId =
      savedLocations[pickupIndex]?.savedLocationId;
  } else {
    newState.enteringNewPickup = true;
    newState.selectedPickupSavedLocationId = undefined;
  }

  if (deliveryIndex !== -1) {
    // We found a match for the delivery location
    newState.enteringNewDelivery = false;
    newState.selectedDeliverySavedLocationId =
      savedLocations[deliveryIndex]?.savedLocationId;
  } else {
    newState.enteringNewDelivery = true;
    newState.selectedDeliverySavedLocationId = undefined;
  }

  // The source shipment is spread wholesale above, so a deadline its location
  // won't show comes along with it and gets re-created on the replacement
  bookShipmentDropHiddenDeadline(newState.shipment, LocationContext.Pickup);
  bookShipmentDropHiddenDeadline(newState.shipment, LocationContext.Delivery);

  // Same reason: a shipment booked before inside was withdrawn from residential
  // still carries it, and re-quoting it must not ask for it again.
  //
  // Rebuilt rather than assigned into. The locations here can still be the
  // caller's own objects — the postal-code blocks above re-spread `newShipment`
  // from scratch, so whichever end they didn't touch is the original reference —
  // and the usual caller passes Redux state, which Immer has deep-frozen. An
  // in-place write to `location.accessorials` throws in strict mode. The
  // deadline helper above gets away with mutating because it only touches
  // top-level fields on this fresh object, never a nested one.
  newState.shipment = {
    ...newState.shipment,
    pickupLocation: {
      ...newState.shipment.pickupLocation,
      accessorials: removeUnavailableAccessorialsForLocation(
        newState.shipment.pickupLocation.locationType,
        newState.shipment.pickupLocation.accessorials,
      ),
    },
    deliveryLocation: {
      ...newState.shipment.deliveryLocation,
      accessorials: removeUnavailableAccessorialsForLocation(
        newState.shipment.deliveryLocation.locationType,
        newState.shipment.deliveryLocation.accessorials,
      ),
    },
  };

  newState.isModifying = true;

  // Capture the source shipment id so the eventual `create2` call can pass it
  // as `replacesId`. The backend uses this to copy `external_metadata` forward
  // and fire `shipment_replaced` through the NetSuite integration channel.
  newState.replacesId = payload.shipment.shipmentId;

  return newState;
}
